import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileBarChart2, Lightbulb, Users, Cpu, Target, Languages } from 'lucide-react';
import { getStoredLanguage, setStoredLanguage } from '../utils/languageUtils';


// Complete content object with all translations
const content = {
  en: {
    nav: {
      back: "Back to Portfolio",
      overview: "Overview",
      challenge: "The Challenge",
      steps: "Initial Steps",
      solution: "MCP Solution",
      results: "Results & Learnings"
    },
    hero: {
      title: "From Zero to Launch: Building with AI",
      subtitle: "A journey of transforming from a non-coder to launching a fully functional website in just 3 days using AI assistance",
      timeline: "Timeline",
      timelineValue: "3 days",
      tools: "Tools Used",
      toolsValue: "MCP, Claude",
      team: "Team Size",
      teamValue: "1 developer",
      role: "Role",
      roleValue: "Product Manager"
    },
    problem: {
      title: "The Challenge",
      description: "As a product manager with no coding experience, I faced significant barriers in creating my own blog and portfolio website:",
      barrier: {
        title: "Technical Barriers",
        content: "HTML, CSS, and JavaScript appeared as alien languages, making the development process overwhelming and seemingly impossible to begin"
      },
      resources: {
        title: "Resource Overload",
        content: "Despite numerous tutorials and resources available, finding a clear starting point proved challenging and disorienting"
      }
    },
    discovery: {
      title: "Initial Steps with AI",
      prototype: {
        title: "First Prototype",
        description: "Discovered and leveraged AI tools to create an initial website prototype:",
        points: [
          "Successfully generated a functioning homepage within an hour",
          "Deployed the initial version to GitHub"
        ]
      },
      challenges: {
        title: "Early Challenges",
        content: "While the initial prototype worked, several issues emerged:\n- Adding new features required starting from scratch\n- Code wasn't modular or maintainable\n- Project management became increasingly difficult"
      }
    },
    solution: {
      title: "The MCP Solution",
      understanding: {
        title: "Understanding MCP",
        description: "Model Context Protocol (MCP) is a framework that helps AI understand the structure and context of a project's code, enabling it to see the big picture and produce seamlessly integrated solutions.",
        tool: {
          title: "MCP-Git-Ingest Tool",
          points: [
            "Analyzes GitHub repositories to create detailed project structures",
            "Identifies modules, components, and dependencies",
            "Builds context for AI to understand project relationships",
            "Improves collaboration between AI and developers"
          ]
        }
      },
      implementation: {
        title: "Implementation",
        content: "Using MCP and AI assistance, I successfully implemented:\n- Auto-generating article directory\n- Modular site layout with reusable components\n- Optimized navigation with sticky navbar\n- Improved page loading times"
      }
    },
    impact: {
      title: "Results & Key Learnings",
      metrics: [
        {
          value: "3 Days",
          label: "From concept to launch"
        },
        {
          value: "100%",
          label: "AI-assisted development"
        },
        {
          value: "0",
          label: "Prior coding experience"
        }
      ],
      learnings: {
        title: "Key Takeaways",
        quote: "This experience taught me that coding isn't the barrier I thought it was. With the right tools and a bit of curiosity, anyone can build a high-quality website—even without a technical background.",
        points: [
          "AI tools can effectively bridge the technical gap for non-developers",
          "MCP methodology provides structure and clarity to AI-assisted development",
          "With proper tools and methodology, complex web development is accessible to everyone"
        ]
      }
    },
    next: {
      label: "Next Project",
      title: "AI-Powered Content Platform →"
    }
  },
  zh: {
    nav: {
      back: "返回作品集",
      overview: "概述",
      challenge: "挑战",
      steps: "初步尝试",
      solution: "MCP 解决方案",
      results: "成果与心得"
    },
    hero: {
      title: "从零开始：借助 AI 构建网站",
      subtitle: "一个产品经理如何在3天内从零基础到成功上线网站的AI辅助开发之旅",
      timeline: "时间线",
      timelineValue: "3天",
      tools: "使用工具",
      toolsValue: "MCP、Claude",
      team: "团队规模",
      teamValue: "1人",
      role: "角色",
      roleValue: "产品经理"
    },
    problem: {
      title: "面临的挑战",
      description: "作为一名没有编程经验的产品经理，在尝试创建个人博客和作品集网站时遇到了重大障碍：",
      barrier: {
        title: "技术门槛",
        content: "HTML、CSS和JavaScript等编程语言对于初学者来说就像天书一样，使得开发过程看起来无比艰巨"
      },
      resources: {
        title: "资源繁杂",
        content: "虽然网上教程资源丰富，但找到一个适合自己的切入点却异常困难，常常让人感到无所适从"
      }
    },
    discovery: {
      title: "AI 辅助尝试",
      prototype: {
        title: "首个原型",
        description: "发现并利用AI工具创建初始网站原型：",
        points: [
          "在一小时内成功生成了一个基础主页",
          "成功部署到GitHub平台"
        ]
      },
      challenges: {
        title: "早期挑战",
        content: "尽管初始原型可以运行，但仍然存在以下问题：\n- 添加新功能需要重新开始\n- 代码结构不够模块化\n- 项目管理变得越来越困难"
      }
    },
    solution: {
      title: "MCP 解决方案",
      understanding: {
        title: "理解MCP",
        description: "模型上下文协议（Model Context Protocol，简称MCP）是一个帮助AI理解项目代码结构和上下文的框架，使其能够从整体角度出发，生成无缝集成的解决方案。",
        tool: {
          title: "MCP-Git-Ingest工具",
          points: [
            "分析GitHub仓库创建详细的项目结构",
            "识别模块、组件和依赖关系",
            "为AI构建项目关系的上下文",
            "提高AI与开发者之间的协作效率"
          ]
        }
      },
      implementation: {
        title: "实施过程",
        content: "使用MCP和AI辅助，成功实现了：\n- 自动生成文章目录\n- 模块化的网站布局与可复用组件\n- 优化的导航栏和页面加载时间"
      }
    },
    impact: {
      title: "成果与启示",
      metrics: [
        {
          value: "3天",
          label: "从构想到上线"
        },
        {
          value: "100%",
          label: "AI辅助开发"
        },
        {
          value: "0",
          label: "前期编程经验"
        }
      ],
      learnings: {
        title: "关键启示",
        quote: "这次经历让我明白，编程并不是我想象中的障碍。只要有合适的工具和一点好奇心，任何人都能构建出高质量的网站——即使没有技术背景。",
        points: [
          "AI工具能有效帮助非开发者跨越技术鸿沟",
          "MCP方法论为AI辅助开发提供了清晰的结构",
          "借助适当的工具和方法，复杂的网站开发对所有人都是可行的"
        ]
      }
    },
    next: {
      label: "下一个项目",
      title: "AI驱动的内容平台 →"
    }
  }
};

const CaseStudy = () => {
  const [activeSection, setActiveSection] = React.useState('hero');

  const [language, setLanguage] = React.useState(getStoredLanguage());

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setStoredLanguage(newLanguage);
  };

  
  React.useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, { threshold: 0.5 });

    const sections = document.querySelectorAll('section[id]');
    sections.forEach(section => observer.observe(section));

    return () => sections.forEach(section => observer.unobserve(section));
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-white">
      {/* Table of Contents */}
      <div className="hidden lg:block fixed right-8 top-32 w-48">
        <div className="mb-4 flex justify-end">
        <button
      onClick={() => handleLanguageChange(language === 'en' ? 'zh' : 'en')}
      className="flex items-center gap-2 px-3 py-1 rounded border border-gray-200 hover:border-gray-300 text-sm"
    >
      <Languages className="w-4 h-4" />
      {language === 'en' ? '中文' : 'English'}
    </button>
        </div>
        <nav className="space-y-2">
          <button 
            onClick={() => scrollToSection('hero')}
            className={`block w-full text-left px-4 py-2 text-sm rounded-lg transition-colors ${
              activeSection === 'hero' 
                ? 'bg-blue-50 text-blue-600' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.nav.overview}
          </button>
          <button 
            onClick={() => scrollToSection('problem')}
            className={`block w-full text-left px-4 py-2 text-sm rounded-lg transition-colors ${
              activeSection === 'problem' 
                ? 'bg-blue-50 text-blue-600' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.nav.challenge}
          </button>
          <button 
            onClick={() => scrollToSection('discovery')}
            className={`block w-full text-left px-4 py-2 text-sm rounded-lg transition-colors ${
              activeSection === 'discovery' 
                ? 'bg-blue-50 text-blue-600' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.nav.steps}
          </button>
          <button 
            onClick={() => scrollToSection('solution')}
            className={`block w-full text-left px-4 py-2 text-sm rounded-lg transition-colors ${
              activeSection === 'solution' 
                ? 'bg-blue-50 text-blue-600' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.nav.solution}
          </button>
          <button 
            onClick={() => scrollToSection('impact')}
            className={`block w-full text-left px-4 py-2 text-sm rounded-lg transition-colors ${
              activeSection === 'impact' 
                ? 'bg-blue-50 text-blue-600' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.nav.results}
          </button>
        </nav>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t.nav.back}
            </Link>
            <button
              onClick={() => setLanguage(language === 'en' ? 'zh' : 'en')}
              className="flex items-center gap-2 px-3 py-1 rounded border border-gray-200 hover:border-gray-300 text-sm md:hidden"
            >
              <Languages className="w-4 h-4" />
              {language === 'en' ? '中文' : 'English'}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            {t.hero.title}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t.hero.subtitle}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">{t.hero.timeline}</p>
              <p className="font-semibold">{t.hero.timelineValue}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">{t.hero.tools}</p>
              <p className="font-semibold">{t.hero.toolsValue}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">{t.hero.team}</p>
              <p className="font-semibold">{t.hero.teamValue}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">{t.hero.role}</p>
              <p className="font-semibold">{t.hero.roleValue}</p>
            </div>
          </div>
        </div>
      </section>

            {/* Challenge */}
      <section id="problem" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Lightbulb className="w-6 h-6 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">{t.problem.title}</h2>
          </div>
          <p className="text-lg text-gray-600 mb-6">
            {t.problem.description}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">{t.problem.barrier.title}</h3>
              <p className="text-gray-600">
                {t.problem.barrier.content}
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">{t.problem.resources.title}</h3>
              <p className="text-gray-600">
                {t.problem.resources.content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Discovery */}
      <section id="discovery" className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">{t.discovery.title}</h2>
          </div>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">{t.discovery.prototype.title}</h3>
              <p className="text-gray-600 mb-4">
                {t.discovery.prototype.description}
              </p>
              <ul className="space-y-2">
                {t.discovery.prototype.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <p className="text-gray-600">{point}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">{t.discovery.challenges.title}</h3>
              <p className="text-gray-600 whitespace-pre-line">
                {t.discovery.challenges.content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section id="solution" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Cpu className="w-6 h-6 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">{t.solution.title}</h2>
          </div>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">{t.solution.understanding.title}</h3>
              <div className="grid grid-cols-1 gap-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-600 mb-6">
                    {t.solution.understanding.description}
                  </p>
                  <h4 className="font-semibold mb-4">{t.solution.understanding.tool.title}</h4>
                  <ul className="space-y-2">
                    {t.solution.understanding.tool.points.map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <p className="text-gray-600">{point}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">{t.solution.implementation.title}</h3>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-600 whitespace-pre-line">
                  {t.solution.implementation.content}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section id="impact" className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <FileBarChart2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">{t.impact.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {t.impact.metrics.map((metric, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                <p className="text-3xl font-bold text-blue-600 mb-2">{metric.value}</p>
                <p className="text-gray-600">{metric.label}</p>
              </div>
            ))}
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-xl font-semibold mb-3">{t.impact.learnings.title}</h3>
            <blockquote className="border-l-4 border-blue-600 pl-4 italic text-gray-600 mb-4">
              {t.impact.learnings.quote}
            </blockquote>
            <p className="text-gray-600">The project demonstrated that:</p>
            <ul className="mt-4 space-y-2">
              {t.impact.learnings.points.map((point, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <p className="text-gray-600">{point}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Next Project */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <p className="text-gray-500 mb-4">{t.next.label}</p>
            <a href="#" className="text-2xl font-semibold text-gray-900 hover:text-blue-600">
              {t.next.title}
            </a>
          </div>
        </div>
      </section>
    
      
    </div>
  );
};

export default CaseStudy;